import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/images/visaro-logo.svg";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { HashLoader } from "react-spinners";
import { useAuth } from "../context/AuthContext";
import {
  inferNextStepFromUser,
  navigateForOnboardingStep,
  pathForNextStep,
} from "../utils/onboardingNextStep";
import type { User } from "../@types";
import use2FA from "../hooks/use2FA";
import { CaretLeft } from "@phosphor-icons/react";
import useResend2FA from "../hooks/useResend2FA";
import { toast } from "sonner";
// import { CaretLeft } from "@phosphor-icons/react";

const TwoFactorAuthentication = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const {
    verify2FA,
    isLoading,
    isError: isVerificationError,
    error: verificationError,
  } = use2FA();
  const { isResendError, isResending, resendError, resendOTP } = useResend2FA();
  const navigate = useNavigate();
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);

  const { user, setUser, refetch, isFetchingUser } = useAuth();
  const tfaID = useMemo(
    () => sessionStorage.getItem("tfaID") || localStorage.getItem("tfaID"),
    [],
  );
  const authenticator =
    sessionStorage.getItem("tfaMethod") === "authenticator" ||
    Boolean(tfaID?.startsWith("totp_"));
  const [useRecovery, setUseRecovery] = useState(false);
  const schema = yup.object().shape({
    code: yup
      .string()
      .required("Code is required")
      .matches(
        authenticator && useRecovery
          ? /^(?:[a-fA-F0-9]{20}|[a-fA-F0-9]{5}(?:-[a-fA-F0-9]{5}){3})$/
          : /^\d{6}$/,
        authenticator && useRecovery
          ? "Enter a complete recovery code"
          : "Enter a 6-digit code",
      ),
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (resendTimer > 0 && resendDisabled) {
      intervalId = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [resendTimer, resendDisabled]);

  useEffect(() => {
    if (!tfaID) {
      navigate("/login");
      return;
    }
  }, [navigate, tfaID]);

  useEffect(() => {
    if (user && !isFetchingUser) {
      if (user.password_set === 0) {
        navigate("/sign-up/create-password");
        return;
      }

      const step = inferNextStepFromUser(user);
      const path = pathForNextStep(step);
      if (path) navigate(path, { replace: true });
      else navigate("/home", { replace: true });
    }
  }, [user, isFetchingUser, navigate]);

  const error = useMemo(
    () => resendError || verificationError,
    [resendError, verificationError],
  );
  const isError = useMemo(
    () => isResendError || isVerificationError,
    [isResendError, isVerificationError],
  );

  const handleResend = () => {
    resendOTP(
      {
        email: localStorage.getItem("email") || "",
      },
      {
        onSuccess(data) {
          if (typeof data.data === "string")
            sessionStorage.setItem("tfaID", data.data);
          setResendDisabled(true);
          setResendTimer(60); // Reset the timer to 60 seconds
          const intervalId = setInterval(() => {
            setResendTimer((prevTimer) => {
              if (prevTimer === 0) {
                clearInterval(intervalId);
                setResendDisabled(false);
              }
              return prevTimer - 1;
            });
          }, 1000);
        },
        onError(error) {
          toast.error(error.message);
        },
      },
    );
  };

  return isFetchingUser ? (
    <div className="h-screen w-screen flex items-center justify-center">
      <HashLoader />
    </div>
  ) : (
    <div>
      {(isLoading || isResending) && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        initialValues={{
          code: "",
          id: tfaID!,
        }}
        validationSchema={schema}
        onSubmit={async (values, { resetForm }) => {
          verify2FA(values, {
            onSuccess: (data) => {
              if (data.data) {
                const payload = data.data as {
                  token: string;
                  user: User;
                  nextStep?: string;
                };
                localStorage.setItem("token", payload.token);
                sessionStorage.removeItem("tfaID");
                sessionStorage.removeItem("tfaMethod");
                localStorage.removeItem("tfaID");
                setUser(payload.user);
                void refetch();
                navigateForOnboardingStep(
                  navigate,
                  payload.nextStep,
                  payload.user,
                );
                resetForm();
              }
            },
          });
        }}
      >
        {({ isValid, dirty, setFieldValue }) => (
          <div className="w-screen h-screen grid grid-cols-1 md:grid-cols-2 ">
            <div className="flex justify-between flex-col h-screen">
              <nav className="p-4 md:px-10 flex items-center">
                <Link to="/">
                  <img src={logo} alt="Visaro's Logo" />
                </Link>
              </nav>
              <div className="flex flex-col justify-center items-start gap-8 text-lg w-full px-5 md:px-20 flex-1">
                <Form className="w-full flex-col gap-5 flex items-center justify-center text-base ">
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="p-3 text-center text-deepBlue border rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start mb-5"
                    >
                      <CaretLeft weight="bold" size={20} />
                    </button>
                    <div className="text-4xl md:text-3xl lg:text-5xl font-bold">
                      Verify your access
                    </div>
                    <div className="text-gray-500">
                      {authenticator
                        ? useRecovery
                          ? "Enter one unused recovery code you saved when you enabled your authenticator."
                          : "Open your authenticator app and enter the current code for Visaro."
                        : "Check your email for the verification code to finish signing in."}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <label
                      htmlFor="code"
                      className="text-sm font-bold text-gray-700"
                    >
                      {authenticator && useRecovery
                        ? "Recovery code*"
                        : "Code*"}
                    </label>
                    <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                      <Field
                        autoComplete="one-time-code"
                        type="text"
                        id="code"
                        name="code"
                        placeholder={
                          authenticator && useRecovery
                            ? "xxxxx-xxxxx-xxxxx-xxxxx"
                            : "Enter 6 digit code"
                        }
                        inputMode={
                          authenticator && useRecovery ? "text" : "numeric"
                        }
                        maxLength={authenticator && useRecovery ? 23 : 6}
                        className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                      />
                    </div>
                    <ErrorMessage
                      name="code"
                      component="div"
                      className="text-red-500"
                    />
                    {error?.validationErrors?.code && (
                      <div className="text-red-500">
                        {error.validationErrors.code}
                      </div>
                    )}
                    {!authenticator && resendDisabled && (
                      <div className="text-gray-500">
                        Resend in {resendTimer} seconds
                      </div>
                    )}
                    {!authenticator && !resendDisabled && (
                      <div className="text-gray-500">
                        Didn’t get a code?{" "}
                        <span
                          className="underline"
                          onClick={handleResend}
                          style={{ cursor: "pointer" }}
                        >
                          Click to resend.
                        </span>
                      </div>
                    )}
                    {authenticator && (
                      <button
                        type="button"
                        className="self-start text-sm underline text-deepBlue"
                        onClick={() => {
                          setUseRecovery((value) => !value);
                          void setFieldValue("code", "");
                        }}
                      >
                        {useRecovery
                          ? "Use my authenticator app"
                          : "Use a recovery code instead"}
                      </button>
                    )}
                    {authenticator && (
                      <p className="text-sm text-gray-500">
                        Email codes cannot replace your authenticator. If this
                        sign-in expires, return to login. After five failed
                        attempts, wait five minutes before signing in again.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    {isError && (
                      <div className="text-red-500 text-left flex">
                        {error?.message}
                      </div>
                    )}
                    <button
                      disabled={!isValid || !dirty || isLoading}
                      type="submit"
                      className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                    >
                      {isLoading ? "Loading" : "Verify"}
                    </button>
                  </div>
                  <div className="flex flex-col gap-2 max-w-md w-full">
                    <Link to="/sign-up" className="text-center text-gray-600">
                      Don't have an account{" "}
                      <span className="text-visaro-orange">Sign Up</span>
                    </Link>
                  </div>
                </Form>
              </div>
            </div>
            <div className="h-screen overflow-hidden hidden md:block">
              <img
                src={require("../assets/images/visaro-man-calling.png")}
                className="h-screen w-full object-cover"
                alt=""
              />
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default TwoFactorAuthentication;
